import { ref, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { ConfirmDeleteModal } from '@/shared/ui';
import { useCustomToast } from '@/shared/lib';
import { trainingContentApi, type TrainingContentListItem } from '@/entities/Training';

export const TRAINING_CONTENT_DELETE_CONFIRM_TITLE = 'Tem certeza que deseja deletar o Conteúdo?';
export const TRAINING_CONTENT_DELETE_CONFIRM_DESCRIPTION = 'A ação não pode ser desfeita.';
export const TRAINING_CONTENT_DELETE_SUCCESS_MESSAGE = 'Conteúdo deletado com sucesso';
export const TRAINING_CONTENT_DELETE_ERROR_MESSAGE = 'Erro ao deletar Conteúdo. Tente novamente';

interface UseDeleteTrainingContentOptions {
  onDeleted: () => void | Promise<void>;
}

export function useDeleteTrainingContent(options: UseDeleteTrainingContentOptions): {
  isDeleting: Ref<boolean>;
  requestDelete: (content: TrainingContentListItem) => void;
} {
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const isDeleting = ref(false);
  const contentToDelete = ref<TrainingContentListItem | null>(null);

  const confirmDelete = async () => {
    if (isDeleting.value || !contentToDelete.value) return;

    isDeleting.value = true;

    try {
      await trainingContentApi.deleteTrainingContent(contentToDelete.value.id);
      deleteModal.close();
      contentToDelete.value = null;
      showSuccessToast({ title: TRAINING_CONTENT_DELETE_SUCCESS_MESSAGE, type: 'success' });
      await options.onDeleted();
    } catch {
      showErrorToast(
        { title: TRAINING_CONTENT_DELETE_ERROR_MESSAGE, type: 'error' },
        { timeout: false, closeButton: true }
      );
    } finally {
      isDeleting.value = false;
    }
  };

  const deleteModal = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      title: TRAINING_CONTENT_DELETE_CONFIRM_TITLE,
      description: TRAINING_CONTENT_DELETE_CONFIRM_DESCRIPTION,
      get isDeleting() {
        return isDeleting.value;
      },
      onClose() {
        if (isDeleting.value) return;
        contentToDelete.value = null;
        deleteModal.close();
      },
      async onConfirm() {
        await confirmDelete();
      },
    },
  });

  const requestDelete = (content: TrainingContentListItem) => {
    contentToDelete.value = content;
    deleteModal.open();
  };

  return { isDeleting, requestDelete };
}
